export interface ToleranceSettings {
    tempControllerToleranceC: number;
    speedTolerancePct: number;
    screwRpmTolerancePct: number;
    ovenZoneToleranceC: number;
    mwPowerToleranceKw: number;
}

// 'absolute' — toleranceValue is added/subtracted directly from the setpoint (right for °C and
// KW categories, where the number is a flat physical offset). 'percent' — toleranceValue is a
// percentage OF the setpoint's own value at each point, needed for Puller speed (m/min) where
// "±0.1%" means a band that scales with the current setpoint, not a flat 0.1 m/min.
export type ToleranceMode = 'absolute' | 'percent';

export interface CategoryRule {
    matchDescription: RegExp;
    matchUnit: string;
    settingsKey: keyof ToleranceSettings;
    toleranceMode: ToleranceMode;
}

export interface ResolvedTolerance {
    value: number;
    mode: ToleranceMode;
}

// The seed defaults below are the offline fallback. LT-3221 makes db_api the source of
// truth: setToleranceSource() hydrates the active rules/settings from the facility-scoped
// chart_tolerance_settings row (fetched once per facility load), so console and mobile stop
// drawing different bands. These constants match that seed so the fallback agrees too.
export const DEFAULT_CATEGORY_RULES: CategoryRule[] = [
    {
        matchDescription: /barrel|head|die|screw|feed|adapter|hose|clamp/i,
        matchUnit: '°C',
        settingsKey: 'tempControllerToleranceC',
        toleranceMode: 'absolute',
    },
    // Line speeds (puller/conveyor/preformer/postformer): the real unit is m/min, but the
    // tolerance IS a percentage applied proportionally to the setpoint, not a flat m/min offset.
    {
        matchDescription: /puller|conveyor|preform|postform/i,
        matchUnit: 'm/min',
        settingsKey: 'speedTolerancePct',
        toleranceMode: 'percent',
    },
    // Screw drive holds tighter than line speed and is separated from the temperature rule by
    // its unit: a "Screw Zone" in °C is a heater, "Screw rpm" is the drive.
    {
        matchDescription: /screw/i,
        matchUnit: 'rpm',
        settingsKey: 'screwRpmTolerancePct',
        toleranceMode: 'percent',
    },
    // Grid row labels for oven zones are abbreviated (e.g. "HotAirCh1"/"HotAirBn1" for
    // Channel/Burner) — match the shared "HotAir" prefix rather than the abbreviated letters,
    // which would be too ambiguous as standalone substrings.
    {
        matchDescription: /oven|burner|channel|hotair/i,
        matchUnit: '°C',
        settingsKey: 'ovenZoneToleranceC',
        toleranceMode: 'absolute',
    },
    { matchDescription: /mw|microwave/i, matchUnit: 'KW', settingsKey: 'mwPowerToleranceKw', toleranceMode: 'absolute' },
];

export const DEFAULT_TOLERANCE_SETTINGS: ToleranceSettings = {
    tempControllerToleranceC: 2,
    speedTolerancePct: 0.1,
    screwRpmTolerancePct: 0.1,
    ovenZoneToleranceC: 10,
    mwPowerToleranceKw: 3,
};

// The raw shape served by db_api (snake_case, match_description as a string). Kept separate
// from the compiled CategoryRule so the case-insensitivity flag is applied here, once, rather
// than baked into the stored pattern (which would not port between Python re and JS RegExp).
export interface ServerCategoryRule {
    match_description: string;
    match_unit: string;
    settings_key: string;
    mode: ToleranceMode;
}
export interface ServerToleranceData {
    settings?: Partial<ToleranceSettings>;
    rules?: ServerCategoryRule[];
}

// Active source, hydrated from the server. Holds the seed until setToleranceSource() runs (or
// if the fetch fails), so charts always have a band source and never regress to "no band".
let activeCategoryRules: CategoryRule[] = DEFAULT_CATEGORY_RULES;
let activeToleranceSettings: ToleranceSettings = DEFAULT_TOLERANCE_SETTINGS;

// Hydrate the active rules/settings from a fetched chart_tolerance_settings data blob. Compiles
// each match_description case-insensitively. On any bad rule the whole set is left unchanged
// (db_api validates on write, but never let a malformed payload blank every band client-side).
export const setToleranceSource = (data: ServerToleranceData | null | undefined): void => {
    if (!data) return;
    if (data.settings) {
        activeToleranceSettings = { ...DEFAULT_TOLERANCE_SETTINGS, ...data.settings };
    }
    if (data.rules && data.rules.length) {
        try {
            activeCategoryRules = data.rules.map((r) => ({
                matchDescription: new RegExp(r.match_description, 'i'),
                matchUnit: r.match_unit,
                settingsKey: r.settings_key as keyof ToleranceSettings,
                toleranceMode: r.mode,
            }));
        } catch {
            // Keep whatever was active; a malformed rule must not remove all bands.
        }
    }
};

// Restore the built-in seed (e.g. on logout or when switching to a facility with no row).
export const resetToleranceSource = (): void => {
    activeCategoryRules = DEFAULT_CATEGORY_RULES;
    activeToleranceSettings = DEFAULT_TOLERANCE_SETTINGS;
};

// First-match-wins walk of `rules` against a row's equipment description/label and unit.
// Returns null when nothing matches (e.g. amps/pressure rows) rather than a fallback number,
// so callers can distinguish "no spec band applies" from "band is zero". Defaults read the
// hydrated active source at call time, so existing call sites need no change.
export const resolveTolerance = (
    equipmentDescription: string,
    unit: string,
    rules: CategoryRule[] = activeCategoryRules,
    settings: ToleranceSettings = activeToleranceSettings,
): ResolvedTolerance | null => {
    const rule = rules.find((r) => r.matchDescription.test(equipmentDescription) && r.matchUnit === unit);
    if (!rule) return null;
    const value = settings[rule.settingsKey];
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return { value, mode: rule.toleranceMode };
};
